import { evaluate, Icon, PulseIndicator } from '@eagle/react-common';
import type { ActivityTimelineItem } from '@ethings/ui-toolkit';
import { ActivityTimeline, Box, Stack } from '@ethings/ui-toolkit';
import { FC, useCallback, useEffect, useMemo, useRef, type JSX } from 'react';
import { TripDrawerTimelineProps } from '../trip-history-base-view.types';
import { VisibleTimelineMarker } from './visible-timeline-marker';
import { areItemIdsEqual, getOrderedVisibleItemIds } from './visible-timeline-marker-utils';

export const TripDrawerTimeline: FC<TripDrawerTimelineProps> = ({ hoveredEventId, itemTemplate, itemTemplateLoaded, items, markerTemplate, markerTemplateLoaded, onHoverEvent, onSelectEvent, onVisibleItemIdsChange, selectedEventId }) => {
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const itemIdsRef = useRef<string[]>([]);
  const lastReportedVisibleItemIdsRef = useRef<string[]>([]);
  const visibleItemIdsRef = useRef<Set<string>>(new Set());
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);
  const timelineItems: ActivityTimelineItem[] = items.map((item) => {
    const itemContext = { event: item.event, item: { color: item.color, label: item.label, subtitle: typeof item.subtitle === 'string' ? item.subtitle : undefined, time: item.time, type: item.type } };
    return {
      color: item.color,
      id: item.id,
      interactive: Boolean(item.event),
      kind: (item.type === 'ongoing' ? 'milestone' : item.type) as ActivityTimelineItem['kind'],
      meta: itemTemplateLoaded ? undefined : item.time,
      subtitle: itemTemplateLoaded ? undefined : item.subtitle,
      title: itemTemplateLoaded && itemTemplate ? evaluate(itemTemplate, itemContext) as string : item.label,
    };
  });

  const reportVisibleItemIds = useCallback(() => {
    const nextVisibleItemIds = getOrderedVisibleItemIds(itemIdsRef.current, visibleItemIdsRef.current);
    if (areItemIdsEqual(lastReportedVisibleItemIdsRef.current, nextVisibleItemIds)) return;

    lastReportedVisibleItemIdsRef.current = nextVisibleItemIds;
    onVisibleItemIdsChange?.(nextVisibleItemIds);
  }, [onVisibleItemIdsChange]);

  const handleVisibleChange = useCallback((itemId: string, visible: boolean) => {
    const visibleItemIds = visibleItemIdsRef.current;
    if (visibleItemIds.has(itemId) === visible) return;

    if (visible) {
      visibleItemIds.add(itemId);
    } else {
      visibleItemIds.delete(itemId);
    }

    reportVisibleItemIds();
  }, [reportVisibleItemIds]);

  useEffect(() => {
    itemIdsRef.current = itemIds;
    visibleItemIdsRef.current = new Set(Array.from(visibleItemIdsRef.current).filter((id) => itemIds.includes(id)));
    reportVisibleItemIds();
  }, [itemIds, reportVisibleItemIds]);

  return (
    <Box ref={scrollContainerRef} style={{ height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      <ActivityTimeline
        items={timelineItems}
        hoveredId={hoveredEventId ?? undefined}
        selectedId={selectedEventId ?? undefined}
        onItemHover={(itemId: string | null) => {
          const hoveredItem = items.find((item) => item.id === itemId);
          if (hoveredItem?.type !== 'event') {
            onHoverEvent(null);
            return;
          }

          onHoverEvent(hoveredItem.event?.eventId ?? null);
        }}
        onItemSelect={(itemId: string) => {
          const selectedItem = items.find((item) => item.id === itemId);
          if (!selectedItem?.event) return;
          onSelectEvent(selectedItem.event);
        }}
        renderMarker={(timelineItem: ActivityTimelineItem) => {
          const item = items.find((entry) => entry.id === timelineItem.id);
          if (!item) return null;

          const markerContext = { event: item.event, item: { color: item.color, type: item.type } };

          if (markerTemplateLoaded && markerTemplate) {
            return (
              <VisibleTimelineMarker itemId={item.id} onVisibleChange={handleVisibleChange} rootRef={scrollContainerRef}>
                {evaluate(markerTemplate, markerContext) as JSX.Element}
              </VisibleTimelineMarker>
            );
          }

          if (item.type === 'event') {
            return (
              <VisibleTimelineMarker
                itemId={item.id}
                onVisibleChange={handleVisibleChange}
                rootRef={scrollContainerRef}
              >
                <Box
                  style={{
                    backgroundColor: item.color,
                    borderRadius: '50%',
                    height: 16,
                    width: 16,
                  }}
                />
              </VisibleTimelineMarker>
            );
          }

          if (item.type === 'ongoing') {
            return (
              <VisibleTimelineMarker
                itemId={item.id}
                onVisibleChange={handleVisibleChange}
                rootRef={scrollContainerRef}
              >
                <PulseIndicator size={16} />
              </VisibleTimelineMarker>
            );
          }

          return (
            <VisibleTimelineMarker
              itemId={item.id}
              onVisibleChange={handleVisibleChange}
              rootRef={scrollContainerRef}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                style={{
                  backgroundColor: item.color,
                  borderRadius: '50%',
                  color: '#fff',
                  height: 24,
                  width: 24,
                }}
              >
                <Icon fontSize="extra-small" icon={item.type === 'start' ? 'ArrowDown' : 'Flag'} />
              </Stack>
            </VisibleTimelineMarker>
          );
        }}
      />
    </Box>
  );
};
